/* eslint-disable indent */
import { useEffect, useMemo, useRef, useState } from "react"
import { Controller, useForm, useWatch } from "react-hook-form"
import { v4 as uuid } from "uuid"
import { Button, InputTel, InputText } from "src/shared/components/forms"
import { InputFoto } from "src/shared/components/forms/input-foto/InputFoto"
import Screen from "src/shared/components/layout/screen/Screen"
import "./RegistroPersonal.css"
import Dropdown from "src/shared/components/forms/selelct-dropdown/Dropdown"
import {
    Colaborador,
    CreateColaboradorInput,
    CreateColaboradorUsuarioInput,
    Puesto,
    useActualizarColaboradorMutation,
    useColaboradoresIdNumQuery,
    useCrearColaboradorConUsuarioMutation,
    useCrearColaboradorMutation,
    useGetAreasQuery,
    useGetPuestosByHotelIdQuery,
} from "src/gql/schema"
import Icon from "src/shared/icons"
import accountBoxFill from "src/shared/icons/accountBoxFill"
import UserContactsFill from "src/shared/icons/UserContactsFill"
import MailFill from "src/shared/icons/MailFill"
import useSnackbar from "src/shared/hooks/useSnackbar"
import { useProfile } from "src/shared/hooks/useProfile"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { togglePersonalDrawer } from "src/store/personal/personal.slice"
import { quitarParentesisYEspacios } from "src/shared/hooks/quitarParentecisySpace"
import { Tooltip } from "src/pages/inventario/producto/form/sections/Tooltip"
import ModalChangeDigitalAuthorization from "src/pages/personal/pages/onboarding-configuracion-digital/components/modal-digital-authorization/ModalChangeDigitalAuthorization"
import ModalConfirmBack from "./components/modal-confirm-back/ModalConfirmBack"
import { uploadFileToBucket } from "src/utils/s3Client"
import { REACT_APP_ARTICULOS_BUCKET } from "src/config/environment"
import { ModalConfirm } from "src/shared/components/layout/modal-confirm/ModalConfirm"
import isRolWithFingerprintAndPIN from "./helpers/isRolWithFingerprintAndPIN"
import getRolPrincipal from "./helpers/getRolPrincipal"
import useLoadingState from "src/shared/hooks/useLoadingState"
import Checkbox from "src/shared/components/forms/checkbox/Checkbox"
import { getPhoneNumberFormat } from "./helpers/phoneNumber"
import PuestosRoles from "../puestos-roles/PuestosRoles"
import { parseJson } from "src/utils/json"
import { setMyProfile } from "src/store/profile/profileSlice"

interface DefaultValues {
    nombre: string
    apellido_materno: string
    apellido_paterno: string
    avatar: string
    NID: string
    puestoOrol: string
    telefono: string
    email: string
    area: string
    es_supervisor: boolean
}

const defaultValues = {
    nombre: "",
    apellido_materno: "",
    apellido_paterno: "",
    avatar: "",
    NID: "",
    puestoOrol: "",
    telefono: "",
    email: "",
    area: "",
    es_supervisor: false,
}

const RegistroPersonal = () => {
    const { data: puestos } = useGetPuestosByHotelIdQuery()
    const { hotel_id } = useProfile()

    const { data: colabsIDNum } = useColaboradoresIdNumQuery({
        variables: {
            hotel_id,
        },
    })

    const { data: areas } = useGetAreasQuery({ variables: { hotel_id } })

    const location = useLocation()
    const navigate = useNavigate()

    const [colaboradorSelected] = useState<Colaborador>(location?.state?.colaboradorSelected)
    const [isShowModalConfirmBack, setIsShowModalConfirmBack] = useState(false)
    const [isModalChangeDigitalAuthorizationOpen, setIsModalChangeDigitalAuthorizationOpen] = useState(false)
    const [hasHuellaChanged, setHasHuellaChanged] = useState(false)

    const [crearColaborador] = useCrearColaboradorMutation()
    const [crearColaboradorConUsuario] = useCrearColaboradorConUsuarioMutation()
    const [actualizarColaborador] = useActualizarColaboradorMutation()
    const dispatch = useDispatch()

    const { showSnackbar } = useSnackbar()

    const [isRoleModalOpen, setIsRoleModalOpen] = useState(false)
    const [isPuestosRolesOpen, setIsPuestosRolesOpen] = useState(false)
    const [roleMessage, setRoleMessage] = useState<string | JSX.Element>("")
    const [roleType, setRoleType] = useState<"noAuthRequired" | "authRequired" | null>(null)

    const handleRoleSelection = (role) => {
        const roles = (puestos?.puestos as Puesto[]) || []
        const roleFromEdit = getRolPrincipal(colaboradorSelected?.colaborador_in_hotel).puestoOrol
        if (!roleFromEdit) {
            return
        }
        if (
            isRolWithFingerprintAndPIN({ selectedRol: roleFromEdit, roles }) &&
            !isRolWithFingerprintAndPIN({ selectedRol: role, roles })
        ) {
            setRoleMessage(
                <>
                    El nuevo rol <strong>no requiere autorización digital</strong>, su PIN y huella dactilar serán
                    eliminados.
                </>
            )
            setRoleType("noAuthRequired")
            setIsRoleModalOpen(true)
        } else if (
            isRolWithFingerprintAndPIN({ selectedRol: roleFromEdit, roles }) &&
            isRolWithFingerprintAndPIN({ selectedRol: role, roles })
        ) {
            setRoleType("authRequired")
            setIsRoleModalOpen(false)
        } else if (
            !isRolWithFingerprintAndPIN({ selectedRol: roleFromEdit, roles }) &&
            isRolWithFingerprintAndPIN({ selectedRol: role, roles })
        ) {
            setRoleMessage(
                <>
                    Este cambio de rol <strong>requiere autorización digital</strong>, por lo tanto debe{" "}
                    <strong>registrar su PIN y huella dactilar.</strong>
                </>
            )
            setRoleType("authRequired")
            setIsRoleModalOpen(true)
        }
    }

    const handleSetDigitalAuth = () => {
        sessionStorage.setItem("personalFormFalues", JSON.stringify(getValues()))
        navigate("/u/onboardingConfiguracionDigital")
    }

    const handleOpenModalChangeDigitalAuthorization = () => {
        setIsModalChangeDigitalAuthorizationOpen(true)
    }

    const valuesFromOnboarding = useMemo(() => {
        let values: typeof defaultValues | undefined = undefined
        if (sessionStorage.getItem("personalFormFalues")) {
            values = JSON.parse(sessionStorage.getItem("personalFormFalues") || "{}")
            sessionStorage.removeItem("personalFormFalues")
        }

        return values
    }, [])

    const [fingerprintsToRegister, setFingerprintsToRegister] = useState<string[] | undefined>(undefined)

    useEffect(() => {
        let values: string[] | undefined = undefined
        if (sessionStorage.getItem("fingerprintData")) {
            values = JSON.parse(sessionStorage.getItem("fingerprintData") || "{}")
            sessionStorage.removeItem("fingerprintData")
        }
        setFingerprintsToRegister(values)
    }, [])

    const snapshotValues = useRef<DefaultValues>(defaultValues)

    const {
        control,
        handleSubmit,
        formState: { errors, isDirty, isSubmitted },
        getValues,
        setValue,
        setError,
        watch,
    } = useForm<DefaultValues>({
        defaultValues: {
            ...(valuesFromOnboarding
                ? valuesFromOnboarding
                : colaboradorSelected
                ? (() => {
                      const rolPrincipal = getRolPrincipal(colaboradorSelected.colaborador_in_hotel)
                      const values = {
                          nombre: colaboradorSelected.nombre,
                          apellido_materno: colaboradorSelected.apellido_materno,
                          apellido_paterno: colaboradorSelected.apellido_paterno,
                          avatar: colaboradorSelected.foto || "",
                          NID: colaboradorSelected.numero_id,
                          puestoOrol: rolPrincipal.puestoOrol,
                          telefono: colaboradorSelected?.telefono_personal ? getPhoneNumberFormat(colaboradorSelected?.telefono_personal) : "",
                          email: colaboradorSelected.correo || "",
                          area: rolPrincipal.areaId,
                          es_supervisor: colaboradorSelected?.es_supervisor || rolPrincipal.esSupervisor,
                          codigo: colaboradorSelected?.usuario?.codigo || ""
                      }

                      snapshotValues.current = values

                      return values
                  })()
                : defaultValues),
        },
    })
    const watchValues = useWatch({ control })
    const hasPinChanged = !!sessionStorage.getItem("PINData")
    const disabledEdicion = JSON.stringify({...snapshotValues.current, cambio_huella: false, cambio_pin: false }) === JSON.stringify({...watchValues, cambio_huella: hasHuellaChanged, cambio_pin: hasPinChanged })

    const areaSelected = watch("area")
    const rolSelected = watch("puestoOrol")
    const rolesDelAreaSeleccionada = useMemo(
        () => (areas?.areas?.find((a) => a.area_id === areaSelected)?.puestos as Puesto[]) || [],
        [areas, areaSelected]
    )
    const isRolSelectedWithFingerprintAndPIN = useMemo(
        () => isRolWithFingerprintAndPIN({ selectedRol: rolSelected || "", roles: puestos?.puestos as Puesto[] }),
        [rolSelected, puestos]
    )

    const nombre = watch("nombre")
    const apellidoPaterno = watch("apellido_paterno")
    const colaboradorName = useMemo(() => `${nombre} ${apellidoPaterno}`, [nombre, apellidoPaterno])

    const isSupervisor = puestos?.puestos?.find((f) => f?.puesto_id === rolSelected)?.nombre === "Supervisor"
    const hasDigitalAuth = !!colaboradorSelected?.usuario_id || !!fingerprintsToRegister?.length

    const { isLoading, isLoadingDelayed, toggleIsLoading } = useLoadingState()

    const handleUpdateFotoPerfil = (colaborador_id: string, foto: string) => {
        const mi_perfil = parseJson(localStorage.getItem("myProfile"), {})
        const colaborador = mi_perfil?.colaborador
        if (colaborador_id === colaborador?.colaborador_id && colaborador?.foto !== foto) {
            const perfil = {...mi_perfil, colaborador: {...colaborador, foto} }
            localStorage.setItem("myProfile", JSON.stringify(perfil))
            dispatch(setMyProfile(perfil))
        }
    }

    const onSubmit = async (data, puestos: Puesto[], areas) => {
        const generatedPin: string | null = sessionStorage.getItem("PINData")

        if (
            (!colaboradorSelected && isRolSelectedWithFingerprintAndPIN && !fingerprintsToRegister && !generatedPin) ||
            isLoading
        ) {
            return
        }
        toggleIsLoading({ value: true })
        const area = areas?.find((a) => a.area_id === data.area)
        const avatar = data.avatar || ""

        const avatarUrl = avatar ? typeof avatar === "string" ? avatar : await uploadFileToBucket({
            bucket: REACT_APP_ARTICULOS_BUCKET ?? "",
            folder: "colaboradores",
            resourceName: uuid().toLowerCase(),
            file: avatar,
        }) : ""

        const values = {
            nombre: data.nombre,
            apellido_materno: data.apellido_materno,
            apellido_paterno: data.apellido_paterno,
            ...(colaboradorSelected ? {} : { hotel_id }),
            foto: avatarUrl || "",
            ...(colaboradorSelected?.numero_id === data.NID ? {} : { numero_id: data.NID }),
            puesto_id: data.puestoOrol,
            telefono_personal: quitarParentesisYEspacios(data.telefono),
            correo: data.email,
            hotel_id,
            area_id: area?.area_id,
            ...(generatedPin ? { codigo: generatedPin } : {}),
            es_supervisor: data?.es_supervisor || false,
        }

        if (colaboradorSelected) {
            actualizarColaborador({
                variables: {
                    datos_colaborador: {
                        ...values,
                        colaborador_id: colaboradorSelected.colaborador_id,
                        ...(isRolWithFingerprintAndPIN({
                            selectedRol: data.puestoOrol,
                            roles: puestos as Puesto[],
                        })
                            ? fingerprintsToRegister
                                ? { template_sample: fingerprintsToRegister }
                                : {}
                            : { template_sample: [] }),
                    },
                },
            })
                .then(({data: res}) => {
                    handleUpdateFotoPerfil(res?.editar_colaborador?.colaborador_id || "", avatarUrl || "")
                    sessionStorage.removeItem("PINData")
                    showSnackbar({
                        status: "success",
                        title: "Actualización de datos",
                        text: `Se actualizaron los datos de **${data.nombre} ${data.apellido_paterno} ${data.apellido_materno}**`,
                    })
                    navigate("/u/tablePerson", { replace: true })
                })
                .catch((e) => {
                    showSnackbar({
                        title: "Error al editar datos",
                        text: e.message || "¡Ups! Se ha producido un error. Por favor, inténtalo nuevamente.",
                        status: "error",
                    })
                })
                .finally(() => {
                    dispatch(togglePersonalDrawer(false))
                    toggleIsLoading({ value: false })
                })
            return
        }
        if (isRolSelectedWithFingerprintAndPIN) {
            crearColaboradorConUsuario({
                variables: {
                    ColaboradorInput: {
                        ...values,
                        ...(fingerprintsToRegister
                            ? { template_sample: fingerprintsToRegister }
                            : { template_sample: [] }),
                    } as CreateColaboradorUsuarioInput,
                },
            })
                .then(() => {
                    showSnackbar({
                        status: "success",
                        title: "Personal registrado",
                        text: `**${data.nombre} ${data.apellido_paterno} ${data.apellido_materno}** ya está registrado exitosamente en tu personal como **${puestos.find((p) => p.puesto_id === data.puestoOrol)?.nombre}**.`,
                    })
                    dispatch(togglePersonalDrawer(false))
                    navigate("/u/tablePerson", { replace: true })
                })
                .catch((e) => {
                    if (e.message?.includes("Ya existe un colaborador con el número id")) {
                        setError("NID", { type: "serverDuplicate" })
                    } else {
                        showSnackbar({
                            title: "Error al registrar personal",
                            text: e.message || "¡Ups! Se ha producido un error. Por favor, inténtalo nuevamente.",
                            status: "error",
                        })
                    }
                })
                .finally(() => {
                    dispatch(togglePersonalDrawer(false))
                    toggleIsLoading({ value: false })
                })
            return
        }
        crearColaborador({
            variables: {
                ColaboradorInput: {
                    ...values,
                } as CreateColaboradorInput,
            },
        })
            .then(() => {
                showSnackbar({
                    status: "success",
                    title: "Personal registrado",
                    text: `**${data.nombre} ${data.apellido_paterno} ${data.apellido_materno}** ya está registrado exitosamente en tu personal como **${puestos.find((p) => p.puesto_id === data.puestoOrol)?.nombre}**.`,
                })
                sessionStorage.removeItem("PINData")
                dispatch(togglePersonalDrawer(false))
                navigate("/u/tablePerson", { replace: true })
            })
            .catch((e) => {
                if (e.message?.includes("Ya existe un colaborador con el número id")) {
                    setError("NID", { type: "serverDuplicate" })
                } else {
                    showSnackbar({
                        title: "Error al registrar personal",
                        text: e.message || "¡Ups! Se ha producido un error. Por favor, inténtalo nuevamente.",
                        status: "error",
                    })
                }
            })
            .finally(() => {
                dispatch(togglePersonalDrawer(false))
                toggleIsLoading({ value: false })
            })
    }

    return (
        <Screen
            title={colaboradorSelected ? "Edición del personal" : "Registro de personal"}
            back={true}
            onBack={() => {
                if (colaboradorSelected && !isDirty) {
                    navigate(-1)
                    dispatch(togglePersonalDrawer(false))
                    return
                }
                setIsShowModalConfirmBack(true)
            }}
        >
            <form
                className="addPerson"
                onSubmit={handleSubmit((data) =>
                    onSubmit(data, puestos?.puestos as Puesto[], areas?.areas)
                )}
            >
                <div className="addPerson__photo">
                    <Controller
                        name="avatar"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <InputFoto
                                pictureSrc={value}
                                onPictureChange={(value) => onChange(value)}
                                modalTitle="Foto del personal"
                                modalSubtitle="Ajusta la imagen del personal en el centro del círculo"
                            />
                        )}
                    />
                </div>
                <div className="addPerson__form">
                    <div className="addPerson__formcontainer">                       
                        <div className="addPerson__form__title">Información personal</div>
                        <div className="addPerson__form__inputs-group">
                            <div className="addPerson__form__inputs-group__fill">
                                <Controller
                                    name="nombre"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { onChange, value } }) => (
                                        <InputText
                                            icon={UserContactsFill}
                                            value={value}
                                            placeholder="Nombre del Personal"
                                            onChange={(data) => onChange(data)}
                                            label={"Nombre"}
                                            type={"text"}
                                            className="addPerson__form__title__input"
                                            error={errors.nombre && true}
                                            errorhinttext={"El nombre es requerido"}
                                        />
                                    )}
                                />
                                <Controller
                                    name="apellido_paterno"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { onChange, value } }) => (
                                        <InputText
                                            icon={UserContactsFill}
                                            value={value}
                                            className="addPerson__form__title__input"
                                            placeholder="Apellido paterno del personal"
                                            onChange={(data) => onChange(data)}
                                            label={"Apellido paterno"}
                                            type={"text"}
                                            error={errors.apellido_paterno && true}
                                            errorhinttext={"El apellido paterno es requerido"}
                                        />
                                    )}
                                />
                                <Controller
                                    name="apellido_materno"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { onChange, value } }) => (
                                        <InputText
                                            icon={UserContactsFill}
                                            value={value}
                                            className="addPerson__form__title__input"
                                            placeholder="Apellido materno del personal"
                                            onChange={(data) => onChange(data)}
                                            label={"Apellido materno"}
                                            type={"text"}
                                            error={errors.apellido_materno && true}
                                            errorhinttext={"El apellido materno es requerido"}
                                        />
                                    )}
                                />
                                <Controller
                                    name="NID"
                                    control={control}
                                    rules={{
                                        required: true,
                                        validate: (v) =>
                                            !colabsIDNum?.colaboradores
                                                ?.filter(
                                                    (c) => c.colaborador_id !== colaboradorSelected?.colaborador_id
                                                )
                                                ?.find((c) => c.numero_id === v),
                                    }}
                                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                                        <InputText
                                            icon={accountBoxFill}
                                            value={value}
                                            placeholder="ID del personal"
                                            className="addPerson__form__title__input"
                                            onChange={(data) => {
                                                onChange(data)
                                            }}
                                            label={"ID del personal"}
                                            type={"text"}
                                            error={!!error}
                                            errorhinttext={
                                                error?.type === "validate" || error?.type === "serverDuplicate"
                                                    ? `Número ID en uso`
                                                    : error?.type === "required"
                                                    ? "Escribe un número de ID"
                                                    : ""
                                            }
                                            tooltipInput={{
                                                title: "Número de ID",
                                                description:
                                                    "Escribe el número de identificación oficial de tu personal. Ej. Número de seguro social",
                                                theme: "dark",
                                            }}
                                            toolTipInfo={true}
                                        />
                                    )}
                                />
                                <div className="addPerson__form__tel">
                                    <Controller
                                        name="telefono"
                                        control={control}
                                        rules={{ required: true, validate: (v) => /^[^_]*$/.test(v) }}
                                        render={({ field: { onChange, value } }) => (
                                            <InputTel
                                                placeholder="(55) 5555 5555"
                                                className="addPerson__form__title__input"
                                                label={"Teléfono personal"}
                                                error={!!errors?.telefono}
                                                errorhinttext={"El teléfono es requerido"}
                                                telValue={value}
                                                prefixValue={"MX +52"}
                                                onPrefixChange={(e) => console.log(e.value)}
                                                onInputChange={(e) => onChange(e)}
                                            />
                                        )}
                                    />
                                </div>
                                <Controller
                                    name="email"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { onChange, value } }) => (
                                        <InputText
                                            value={value}
                                            icon={MailFill}
                                            placeholder="ejemplo@correo.com"
                                            className="addPerson__form__title__input"
                                            label="Correo electrónico"
                                            onChange={(data) => onChange(data)}
                                            style={{ widows: "33%" }}
                                            error={!!errors?.email}
                                            type="text"
                                            errorhinttext={"El email es requerido"}
                                        />
                                    )}
                                />
                            </div>

                        </div>
                    </div>
                    <div className="addPerson__formcontainer">
                        <div className="addPerson__form__title">Información laboral</div>
                        <div className="addPerson__form__inputs-group">
                            <div className="addPerson__form__inputs-group__fill">
                                <Controller
                                    name="area"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                                        <Dropdown
                                            value={value}
                                            icon="buildingTwo"
                                            placeholder="Selecciona una opción"
                                            className="addPerson__form__title__input"
                                            onClick={(data) => {
                                                onChange(data.value)
                                                const rolesDeLaNuevaArea =
                                                    (areas?.areas?.find((a) => a.area_id === data.value)
                                                        ?.puestos as Puesto[]) || []
                                                if (
                                                    rolSelected &&
                                                    !rolesDeLaNuevaArea.some((p) => p?.puesto_id === rolSelected)
                                                ) {
                                                    setValue("puestoOrol", "")
                                                }
                                            }}
                                            label={"Área"}
                                            errorHintText={error ? "El área es requerida" : ""}
                                            options={
                                                areas?.areas.map((p) => ({
                                                    label: p.nombre,
                                                    value: p.area_id,
                                                })) || []
                                            }
                                            iconInOptions={false}
                                        />
                                    )}
                                />
                                <Controller
                                    name="puestoOrol"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { onChange, value } }) => (
                                        <Dropdown
                                            value={value}
                                            icon="SuitcaseFill"
                                            placeholder="Puesto o rol del personal"
                                            className="addPerson__form__title__input"
                                            disabled={!areaSelected}
                                            onClick={(data) => {
                                                onChange(data.value)
                                                handleRoleSelection(data.value)
                                            }}
                                            label={"Puesto o rol"}
                                            tooltip={{ title: "Puestos o roles", theme: "dark" }}
                                            onTooltipClick={() => setIsPuestosRolesOpen(true)}
                                            errorHintText={errors.puestoOrol ? "El puesto es requerido" : ""}
                                            options={rolesDelAreaSeleccionada.map((p) => ({
                                                label: p?.nombre,
                                                value: p?.puesto_id,
                                            }))}
                                            iconInOptions={false}
                                        />
                                    )}
                                />
                                {isRolSelectedWithFingerprintAndPIN && (
                                    <div className="addPersonFingerprint__link__container">
                                        <div
                                            onClick={() => {
                                                if (hasDigitalAuth) {
                                                    handleOpenModalChangeDigitalAuthorization()
                                                } else {
                                                    handleSetDigitalAuth()
                                                }
                                            }}
                                            className="addPersonFingerprint__link__action"
                                        >
                                            <Icon color="var(--primary)" name={hasDigitalAuth ? "editFill" : "plusCircle"} />
                                            <span className="addPersonFingerprint__link__texto">
                                                <span className="addPersonFingerprint__link__bold-text">
                                                    {hasDigitalAuth
                                                        ? "Cambiar PIN y huella dactilar"
                                                        : "Agregar PIN y huella dactilar"}
                                                </span>
                                            </span>
                                        </div>
                                        <div className="addPersonFingerprint__link__tooltip">
                                            <Tooltip
                                                placement={"right"}
                                                title="Huella Dactilar y/o PIN"
                                                description="Esta opción facilita el trabajo de tus colaboradores en tareas cotidianas y te brinda seguridad en tus operaciones críticas"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                            {isRoleModalOpen && (
                                <ModalConfirm
                                    isOpen={isRoleModalOpen}
                                    icon={
                                        <Icon
                                            name="ExclamationFilled"
                                            color="var(--pink-ocupado)"
                                            height={24}
                                            width={24}
                                        />
                                    }
                                    iconTheme="danger"
                                    title="Cambio de rol"
                                    description={roleMessage}
                                    cancelButtonText="Cancelar"
                                    confirmButtonText={
                                        roleType === "authRequired" ? "Registrar PIN y/o huella" : undefined
                                    } // Solo lo cambia si se pasa
                                    onCloseDialog={({ confirmed }) => {
                                        setIsRoleModalOpen(false)
                                        if (!confirmed) {
                                            setValue(
                                                "puestoOrol",
                                                getRolPrincipal(colaboradorSelected?.colaborador_in_hotel).puestoOrol
                                            )
                                            return
                                        }
                                        if (
                                            isRolWithFingerprintAndPIN({
                                                selectedRol: rolSelected,
                                                roles: puestos?.puestos as Puesto[],
                                            })
                                        ) {
                                            sessionStorage.setItem("personalFormFalues", JSON.stringify(getValues()))
                                            navigate("/u/onboardingConfiguracionDigital")
                                        } else {
                                            showSnackbar({
                                                status: "success",
                                                title: "Eliminación de PIN y huella de autorización",
                                                text: `Se eliminó PIN y huella para ${colaboradorSelected?.nombre} ${colaboradorSelected?.apellido_paterno} ${colaboradorSelected?.apellido_materno} exitosamente.`,
                                            })
                                        }
                                    }}
                                />
                            )}

                        </div>
                        <div className="addPersonFingerprint__buttonSubm">
                            <div
                                className="buttonSubm"
                                style={{ justifyContent: isSupervisor ? "flex-end" : "space-between" }}
                            >
                                {!isSupervisor && (
                                    <div className="addPerson__supervisor">
                                        <Controller
                                            name={"es_supervisor"}
                                            control={control}
                                            rules={{ required: false }}
                                            render={({ field: { onChange, value } }) => (
                                                <Checkbox
                                                    className="addPerson__supervisor-check"
                                                    iconColorWhenChecked="var(--white)"
                                                    value={value}
                                                    onChange={onChange}
                                                />
                                            )}
                                        />
                                        <div className="addPerson__supervisor-contain">
                                            <p className="addPerson__supervisor-title">
                                                {"Habilitar funciones de supervisión"}
                                            </p>
                                            <p className="addPerson__supervisor-description">
                                                {
                                                    "Al activar esta opción, el perfil podrá validar habitaciones en estado de supervisión,\nademás de las funciones de su rol principal."
                                                }
                                            </p>
                                        </div>
                                    </div>
                                )}
                                <Button
                                    disabled={isLoadingDelayed || (colaboradorSelected ? disabledEdicion : false)}
                                    text={colaboradorSelected ? "Guardar cambios" : "Registrar personal"}
                                    type="submit"
                                    style={{ width: "305px" }}
                                />
                            </div>
                        </div>
                        {!isRolSelectedWithFingerprintAndPIN && <div className="addPersonFingerprint__error"></div>}
                        {!colaboradorSelected &&
                            isRolSelectedWithFingerprintAndPIN &&
                            isSubmitted &&
                            !fingerprintsToRegister &&
                            !sessionStorage.getItem("PINData") && (
                                <div className="addPersonFingerprint__error">
                                    <span>Por favor ingresa PIN y huella dactilar para continuar</span>
                                </div>
                            )}
                    </div>
                    <ModalChangeDigitalAuthorization
                        colaboradorName={colaboradorName}
                        isOpen={isModalChangeDigitalAuthorizationOpen}
                        onClose={() => setIsModalChangeDigitalAuthorizationOpen(false)}
                        onAuthorizationSave={(d) => {
                            setFingerprintsToRegister(d)
                            setHasHuellaChanged(true)
                        }}
                    />
                </div>
            </form>
            <ModalConfirmBack isOpen={isShowModalConfirmBack} setIsOpen={setIsShowModalConfirmBack} />
            <PuestosRoles isOpen={isPuestosRolesOpen} onClose={() => setIsPuestosRolesOpen(false)} />
        </Screen>
    )
}

export default RegistroPersonal
